import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { api } from '../api.ts';
import { copy } from '../copy/en-CA.ts';
import { openedFromAdmin } from '../nav.ts';
import { useSession } from '../session.tsx';

export function Layout() {
  const { user, setUser } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.isAdmin === true;
  // How many things the admin's To do page lists, for the Admin tab. Asked
  // again on every move to another page, so it follows what the admin has
  // just done elsewhere; null while unknown or for anyone else.
  const [todoCount, setTodoCount] = useState<number | null>(null);
  useEffect(() => {
    if (!isAdmin) {
      setTodoCount(null);
      return;
    }
    let cancelled = false;
    api.admin
      .todo()
      .then((r) => {
        if (!cancelled) setTodoCount(r.items.length);
      })
      .catch(() => {
        // The count is a courtesy; the To do page itself reports the real error.
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, location.pathname]);
  if (!user) return null;

  // A schedule page belongs to History, or to Admin when opened from there,
  // so the tab bar never loses track of where the person is.
  const onSchedulePage = location.pathname.startsWith('/schedules/');
  const fromAdmin = onSchedulePage && openedFromAdmin(location.state);
  const historyCurrent = location.pathname === '/history' || (onSchedulePage && !fromAdmin);
  const adminCurrent = location.pathname === '/admin' || location.pathname.startsWith('/admin/') || fromAdmin;

  const signOut = async () => {
    await api.signOut();
    setUser(null);
    await navigate('/');
  };

  // On a phone the header is two rows (app.css): the app name with the person
  // and Sign out, then the tabs. The order here stays name, tabs, person, so
  // keyboard focus and a screen reader go through it the same way at any width.
  return (
    <>
      <header className="app-header">
        <a className="wordmark" href="/">
          <span className="wordmark-name">{copy.appName}</span>
          <span className="wordmark-sub">{copy.appSub}</span>
        </a>
        <nav className="app-nav" aria-label="Sections">
          <NavLink to="/" end>
            {copy.nav.schedule}
          </NavLink>
          <Link to="/history" aria-current={historyCurrent ? 'page' : undefined}>
            {copy.nav.history}
          </Link>
          {/* "Calendar" on a phone, so every tab fits one row; its name stays "Add to calendar" for everyone. */}
          <NavLink to="/calendar" aria-label={copy.nav.calendar}>
            <span className="nav-full">{copy.nav.calendar}</span>
            <span className="nav-short" aria-hidden="true">
              {copy.nav.calendarShort}
            </span>
          </NavLink>
          {user.physicianId ? <NavLink to="/requests">{copy.nav.requests}</NavLink> : null}
          {user.isAdmin ? (
            // With anything waiting: "Admin 3", named "Admin, 3 things to do". On a
            // phone the number sits on the tab's corner (app.css), so the tabs keep one row.
            <Link
              to="/admin/todo"
              className="admin-tab"
              aria-current={adminCurrent ? 'page' : undefined}
              aria-label={todoCount ? copy.nav.adminWaiting(todoCount) : undefined}
            >
              {copy.nav.admin}
              {todoCount ? (
                <span className="tab-count" aria-hidden="true">
                  {String(todoCount)}
                </span>
              ) : null}
            </Link>
          ) : null}
        </nav>
        <div className="header-user">
          <div className="header-who">
            <span>
              {/* On a phone only the name shows, so the actions fit beside it; a screen reader still hears it all. */}
              <span className="who-extra">{copy.signedInAs} </span>
              <strong>{user.displayName}</strong>
              <span className="who-extra">
                {' '}
                · {copy.roles[user.role]}
                {user.isAdmin && user.physicianId ? ` · ${copy.roles.physician}` : ''}
              </span>
            </span>
            <NavLink to="/password" className="header-link">
              {copy.changePasswordLink}
            </NavLink>
          </div>
          <button
            type="button"
            className="btn small"
            onClick={() => {
              void signOut();
            }}
          >
            {copy.signOut}
          </button>
        </div>
      </header>
      <main className="page" id="main">
        <Outlet />
      </main>
    </>
  );
}
